// UserLayout.tsx

import React, { useEffect, useRef, useState } from "react";

import Footer from "../components/Footer";
import UserHeader from "./UserHeader";
import UserLoader from "./UserLoader";

import { putUserLogin } from "../handler/LoginAndSingupHandler";

import "../Dashboard/Design/Dashboard.css";
import "../Design/Responsive.css";

interface LayoutProps {
  children: React.ReactNode;
}

interface UserInternship {
  userInternshipId: number;
  internshipId: number;
  internshipName: string;
  internshipDescription: string;
  startDate: string;
  endDate: string;
  internshipDays: number;
}

const UserLayout: React.FC<LayoutProps> = ({ children }) => {
  const hasRun = useRef(false);

  const internshipRef =
    useRef<HTMLDivElement | null>(null);

  const [loader, setLoader] = useState(true);

  const [userInternship, setUserInternship] =
    useState<UserInternship | null>(null);

  const [internshipExpanded, setInternshipExpanded] =
    useState(false);

  // =========================================================
  // DATE HELPERS
  // =========================================================

  const parseDate = (date: string): Date => {
    if (!date) {
      return new Date(NaN);
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] =
        date.split("-").map(Number);

      return new Date(
        year,
        month - 1,
        day
      );
    }

    return new Date(date);
  };

  const normalizeDate = (date: Date): Date => {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
  };

  const formatDate = (date: string): string => {
    const parsedDate = parseDate(date);

    if (isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  const getRemainingDays = (
    endDate: string
  ): number => {
    const end = normalizeDate(
      parseDate(endDate)
    );

    const today = normalizeDate(
      new Date()
    );

    if (isNaN(end.getTime())) {
      return 0;
    }

    const difference =
      end.getTime() -
      today.getTime();

    return Math.ceil(
      difference /
        (1000 * 60 * 60 * 24)
    );
  };

  const getInternshipProgress = (
    startDate: string,
    endDate: string
  ): number => {
    const start = normalizeDate(
      parseDate(startDate)
    );

    const end = normalizeDate(
      parseDate(endDate)
    );

    const today = normalizeDate(
      new Date()
    );

    if (
      isNaN(start.getTime()) ||
      isNaN(end.getTime())
    ) {
      return 0;
    }

    const totalDuration =
      end.getTime() -
      start.getTime();

    if (totalDuration <= 0) {
      return today.getTime() >=
        end.getTime()
        ? 100
        : 0;
    }

    const elapsed =
      today.getTime() -
      start.getTime();

    const progress =
      (elapsed /
        totalDuration) *
      100;

    return Math.min(
      100,
      Math.max(0, progress)
    );
  };

  // =========================================================
  // LOGIN / REFRESH
  // =========================================================

  const userLoginOnRefresh =
    async () => {
      try {
        const response =
          await putUserLogin({
            growniverseId: "",
            password: "",
          });

        if (
          response.statusCode === 200
        ) {
          const userData =
            response.data.loginResponse;

          const pathName =
            window.location.pathname;

          localStorage.setItem(
            "userInternship",
            JSON.stringify(
              userData.userInternship
            )
          );

          localStorage.setItem(
            "appkey",
            userData.appKey
          );

          localStorage.setItem(
            "userProfile",
            JSON.stringify(
              userData.profile
            )
          );

          localStorage.setItem(
            "account",
            JSON.stringify(
              userData.account
            )
          );

          if (
            userData.profile.packageId ===
            0
          ) {
            localStorage.setItem(
              "packages",
              JSON.stringify(
                userData.packages
              )
            );
          }

          setUserInternship(
            userData.userInternship ??
              null
          );

          if (
            pathName !== "/package" &&
            pathName !== "/hold" &&
            pathName !==
              "/packageDetail" &&
            pathName !== "/payment"
          ) {
            if (
              userData.profile.status ===
              2
            ) {
              window.location.href =
                "/package";

              return;
            }

            if (
              userData.profile.status ===
              3
            ) {
              window.location.href =
                "/hold";

              return;
            }

            if (
              !userData.profile
                .affiliateApproved &&
              pathName !==
                "/mycourses" &&
              pathName !==
                "/profile" &&
              pathName !==
                "/mywallet" &&
              pathName !== "/event" &&
              pathName !== "/support"
            ) {
              window.location.href =
                "/profile";

              return;
            }
          }

          else if (
            pathName === "/payment"
          ) {
            if (
              userData.profile.status >=
                4 &&
              userData.profile
                .affiliateApproved
            ) {
              window.location.href =
                "/dashboard";

              return;
            }

            if (
              !userData.profile
                .affiliateApproved
            ) {
              window.location.href =
                "/mycourses";

              return;
            }
          }

          else if (
            pathName === "/hold"
          ) {
            if (
              userData.profile.status <
              3
            ) {
              window.location.href =
                "/package";

              return;
            }
          }

          if (
            userData.profile.isAdmin
          ) {
            window.location.href =
              "/packagepayment";

            return;
          }

          setLoader(false);
        }

        else if (
          response.statusCode ===
          409
        ) {
          localStorage.clear();

          window.location.href = "/";
        }

        else {
          setLoader(false);
        }
      }

      catch (error) {
        console.error(
          "User refresh login failed:",
          error
        );

        setLoader(false);
      }
    };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    if (hasRun.current) {
      return;
    }

    const appKey =
      localStorage.getItem(
        "appkey"
      );

    if (!appKey) {
      window.location.href = "/";

      return;
    }

    userLoginOnRefresh();

    hasRun.current = true;
  }, []);

  // =========================================================
  // INTERNSHIP OUTSIDE CLICK / SCROLL
  // =========================================================

  useEffect(() => {
    if (!internshipExpanded) {
      return;
    }

    const handleOutsideClick = (
      event: MouseEvent
    ) => {
      if (
        internshipRef.current &&
        !internshipRef.current.contains(
          event.target as Node
        )
      ) {
        setInternshipExpanded(
          false
        );
      }
    };

    const handleScroll = () => {
      setInternshipExpanded(
        false
      );
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    window.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      }
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );

      window.removeEventListener(
        "scroll",
        handleScroll
      );
    };
  }, [internshipExpanded]);

  // =========================================================
  // INTERNSHIP VALUES
  // =========================================================

  const remainingDays =
    userInternship
      ? getRemainingDays(
          userInternship.endDate
        )
      : 0;

  const internshipProgress =
    userInternship
      ? getInternshipProgress(
          userInternship.startDate,
          userInternship.endDate
        )
      : 0;

  const isExpired =
    remainingDays < 0;

  const internshipStatus =
    isExpired
      ? "EXPIRED"
      : remainingDays === 0
      ? "ENDS TODAY"
      : "ACTIVE";

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="growniverse-user-layout">

      {loader && <UserLoader />}

      {!loader && (
        <>
          {/* =================================================
              HEADER
          ================================================= */}

          <div className="growniverse-fixed-header">
            <UserHeader />
          </div>


          {/* =================================================
              INTERNSHIP BUTTON / PANEL
          ================================================= */}

          {userInternship && (
            <div
              ref={internshipRef}
              className="growniverse-internship-bar"
              onMouseEnter={() =>
                setInternshipExpanded(true)
              }
            >

              {!internshipExpanded ? (

                /* =================================================
                   SMALL SQUARE BUTTON
                ================================================= */

                <button
                  type="button"
                  className="internship-square-button"
                  onClick={() =>
                    setInternshipExpanded(
                      true
                    )
                  }
                  aria-label="View internship details"
                >
                  <i className="fas fa-graduation-cap" />

                  <span
                    className={`internship-square-dot ${
                      isExpired
                        ? "expired"
                        : ""
                    }`}
                  />
                </button>

              ) : (

                /* =================================================
                   LARGE PROFESSIONAL INTERNSHIP PANEL
                ================================================= */

                <div
                  className="internship-full-card"
                  onClick={(event) =>
                    event.stopPropagation()
                  }
                >

                  {/* TOP ACCENT */}
                  <div className="internship-card-accent" />


                  {/* GLOW EFFECTS */}
                  <div className="internship-card-glow internship-card-glow-one" />

                  <div className="internship-card-glow internship-card-glow-two" />


                  {/* =================================================
                      CARD HEADER
                  ================================================= */}

                  <div className="internship-card-header">

                    <div className="internship-card-identity">

                      <div className="internship-large-icon">

                        <div className="internship-large-icon-ring" />

                        <i className="fas fa-graduation-cap" />

                      </div>


                      <div className="internship-heading">

                        <div className="internship-overline">
                          <span className="internship-live-indicator" />

                          YOUR INTERNSHIP
                        </div>

                        <h3>
                          {userInternship.internshipName ||
                            "Professional Internship"}
                        </h3>

                      </div>

                    </div>


                    {/* CLOSE */}

                    <button
                      type="button"
                      className="internship-close-button"
                      onClick={() =>
                        setInternshipExpanded(
                          false
                        )
                      }
                      aria-label="Close internship"
                    >
                      <i className="fas fa-times" />
                    </button>

                  </div>


                  {/* =================================================
                      DESCRIPTION
                  ================================================= */}

                  {userInternship.internshipDescription && (
                    <div className="internship-description">

                      <div className="internship-description-icon">
                        <i className="fas fa-sparkles" />
                      </div>

                      <div>

                        <span className="internship-section-label">
                          ABOUT YOUR INTERNSHIP
                        </span>

                        <p>
                          {
                            userInternship.internshipDescription
                          }
                        </p>

                      </div>

                    </div>
                  )}


                  {/* =================================================
                      MAIN DATE INFORMATION
                  ================================================= */}

                  <div className="internship-main-info">

                    {/* START */}

                    <div className="internship-info-card">

                      <div className="internship-info-card-icon">
                        <i className="far fa-calendar-alt" />
                      </div>

                      <div className="internship-info-card-content">

                        <span>
                          START DATE
                        </span>

                        <strong>
                          {formatDate(
                            userInternship.startDate
                          )}
                        </strong>

                      </div>

                    </div>


                    {/* CONNECTOR */}

                    <div className="internship-date-connector">
                      <span />
                      <i className="fas fa-arrow-right" />
                      <span />
                    </div>


                    {/* END */}

                    <div className="internship-info-card">

                      <div className="internship-info-card-icon">
                        <i className="far fa-calendar-check" />
                      </div>

                      <div className="internship-info-card-content">

                        <span>
                          END DATE
                        </span>

                        <strong>
                          {formatDate(
                            userInternship.endDate
                          )}
                        </strong>

                      </div>

                    </div>


                    {/* DURATION */}

                    <div className="internship-info-card internship-duration-card">

                      <div className="internship-info-card-icon">
                        <i className="far fa-clock" />
                      </div>

                      <div className="internship-info-card-content">

                        <span>
                          DURATION
                        </span>

                        <strong>
                          {
                            userInternship.internshipDays
                          }{" "}
                          Days
                        </strong>

                      </div>

                    </div>

                  </div>


                  {/* =================================================
                      PROGRESS
                  ================================================= */}

                  <div className="internship-progress-box">

                    <div className="internship-progress-top">

                      <div>

                        <span className="internship-section-label">
                          INTERNSHIP JOURNEY
                        </span>

                        <strong>
                          {Math.round(
                            internshipProgress
                          )}
                          % Complete
                        </strong>

                      </div>

                      <div className="internship-progress-percent">
                        {Math.round(
                          internshipProgress
                        )}
                        %
                      </div>

                    </div>


                    <div className="internship-progress-track">

                      <div
                        className="internship-progress-fill"
                        style={{
                          width: `${internshipProgress}%`,
                        }}
                      >
                        <span />
                      </div>

                    </div>


                    <div className="internship-progress-bottom">

                      <span>
                        {formatDate(
                          userInternship.startDate
                        )}
                      </span>

                      <span>
                        {formatDate(
                          userInternship.endDate
                        )}
                      </span>

                    </div>

                  </div>


                  {/* =================================================
                      STATUS / REMAINING
                  ================================================= */}

                  <div
                    className={`internship-bottom-section ${
                      isExpired
                        ? "internship-bottom-expired"
                        : ""
                    }`}
                  >

                    <div className="internship-status-main">

                      <div className="internship-status-circle">

                        <i
                          className={
                            isExpired
                              ? "fas fa-check"
                              : "fas fa-bolt"
                          }
                        />

                      </div>


                      <div className="internship-status-text">

                        <span>
                          SUBSCRIPTION STATUS
                        </span>

                        <strong>
                          {internshipStatus}
                        </strong>

                      </div>

                    </div>


                    <div className="internship-remaining">

                      <span>
                        {isExpired
                          ? "COMPLETED"
                          : remainingDays ===
                            0
                          ? "TODAY"
                          : "REMAINING"}
                      </span>

                      <strong>
                        {isExpired
                          ? `${Math.abs(
                              remainingDays
                            )} days ago`
                          : remainingDays ===
                            0
                          ? "Final Day"
                          : `${remainingDays} Days`}
                      </strong>

                    </div>

                  </div>

                </div>
              )}

            </div>
          )}


          {/* =================================================
              PAGE CONTENT
          ================================================= */}

          <div
            className={`growniverse-layout-content ${
              userInternship
                ? "has-internship"
                : ""
            }`}
          >

            <main>
              {children}
            </main>

            <Footer />

          </div>

        </>
      )}


      {/* =====================================================
          STYLES
      ===================================================== */}

      <style>
        {`

/* =========================================================
   ROOT
========================================================= */

.growniverse-user-layout {
  position: relative;

  width: 100%;
  min-height: 100vh;

  margin: 0;
  padding: 0;

  background:
    radial-gradient(
      circle at 15% 10%,
      rgba(0, 246, 255, 0.035),
      transparent 28%
    ),
    radial-gradient(
      circle at 85% 15%,
      rgba(139, 92, 246, 0.035),
      transparent 30%
    ),
    linear-gradient(
      180deg,
      #05070d 0%,
      #070b14 45%,
      #05070d 100%
    );

  color: #ffffff;

  overflow-x: hidden;
}


/* =========================================================
   HEADER
========================================================= */

.growniverse-fixed-header {
  position: fixed;

  top: 0;
  left: 0;
  right: 0;

  width: 100%;

  z-index: 1200;

  margin: 0;
  padding: 0;

  background:
    rgba(5, 8, 16, 0.96);

  backdrop-filter:
    blur(18px);

  -webkit-backdrop-filter:
    blur(18px);

  box-shadow:
    0 8px 35px
    rgba(0, 0, 0, 0.45);
}


/* =========================================================
   INTERNSHIP FLOATING BUTTON
========================================================= */

.growniverse-internship-bar {
  position: fixed;

  top: 88px;
  right: 20px;

  z-index: 1190;

  width: auto;

  pointer-events: none;
}

.growniverse-internship-bar > * {
  pointer-events: auto;
}


/* =========================================================
   SMALL SQUARE
========================================================= */

.internship-square-button {

  position: relative;

  width: 42px;
  height: 42px;

  display: flex;

  align-items: center;
  justify-content: center;

  padding: 0;

  border:
    1px solid
    rgba(245, 180, 95, 0.38);

  border-radius: 11px;

  background:
    linear-gradient(
      145deg,
      rgba(29, 26, 22, 0.98),
      rgba(7, 10, 16, 0.98)
    );

  color: #f5b45f;

  cursor: pointer;

  box-shadow:
    0 10px 30px
      rgba(0, 0, 0, 0.5),
    0 0 22px
      rgba(245, 180, 95, 0.08),
    inset 0 1px 0
      rgba(255, 255, 255, 0.08);

  transition:
    transform 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease;
}

.internship-square-button:hover {

  transform:
    translateY(-2px)
    scale(1.06);

  border-color:
    rgba(245, 180, 95, 0.8);

  box-shadow:
    0 12px 35px
      rgba(0, 0, 0, 0.6),
    0 0 30px
      rgba(245, 180, 95, 0.18),
    inset 0 1px 0
      rgba(255, 255, 255, 0.1);
}

.internship-square-button i {
  font-size: 17px;
}


/* STATUS DOT */

.internship-square-dot {

  position: absolute;

  top: 5px;
  right: 5px;

  width: 8px;
  height: 8px;

  border-radius: 50%;

  background: #72e6a5;

  box-shadow:
    0 0 0 2px #080b11,
    0 0 10px
      rgba(114, 230, 165, 0.8);

  animation:
    internshipPulse
    2s ease-in-out infinite;
}

.internship-square-dot.expired {

  background: #ff7373;

  box-shadow:
    0 0 0 2px #080b11,
    0 0 10px
      rgba(255, 115, 115, 0.8);
}


/* =========================================================
   FULL CARD
========================================================= */

.internship-full-card {

  position: relative;

  width: 680px;

  max-width:
    calc(100vw - 30px);

  padding: 22px;

  overflow: hidden;

  border-radius: 20px;

  background:
    linear-gradient(
      145deg,
      rgba(19, 22, 31, 0.99),
      rgba(7, 10, 17, 0.99)
    );

  border:
    1px solid
    rgba(245, 180, 95, 0.25);

  box-shadow:
    0 25px 70px
      rgba(0, 0, 0, 0.7),
    0 0 50px
      rgba(245, 180, 95, 0.06),
    inset 0 1px 0
      rgba(255, 255, 255, 0.07);

  backdrop-filter:
    blur(24px);

  -webkit-backdrop-filter:
    blur(24px);

  animation:
    internshipExpand
    0.2s ease-out;
}


/* TOP GOLD LINE */

.internship-card-accent {

  position: absolute;

  top: 0;
  left: 10%;
  right: 10%;

  height: 2px;

  background:
    linear-gradient(
      90deg,
      transparent,
      #f5b45f,
      #ffe1ad,
      #f5b45f,
      transparent
    );

  opacity: 0.8;
}


/* GLOW */

.internship-card-glow {

  position: absolute;

  width: 180px;
  height: 180px;

  border-radius: 50%;

  filter: blur(70px);

  opacity: 0.07;

  pointer-events: none;
}

.internship-card-glow-one {

  top: -120px;
  left: 20%;

  background: #f5b45f;
}

.internship-card-glow-two {

  bottom: -130px;
  right: 10%;

  background: #8b5cf6;
}


/* =========================================================
   HEADER
========================================================= */

.internship-card-header {

  position: relative;

  z-index: 2;

  display: flex;

  align-items: center;

  justify-content: space-between;

  padding-bottom: 17px;

  border-bottom:
    1px solid
    rgba(255, 255, 255, 0.07);
}

.internship-card-identity {

  display: flex;

  align-items: center;

  gap: 14px;

  min-width: 0;
}


/* LARGE ICON */

.internship-large-icon {

  position: relative;

  width: 54px;
  height: 54px;

  flex: 0 0 54px;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 15px;

  color: #f5b45f;

  background:
    linear-gradient(
      145deg,
      rgba(245, 180, 95, 0.14),
      rgba(245, 180, 95, 0.035)
    );

  border:
    1px solid
    rgba(245, 180, 95, 0.3);

  box-shadow:
    inset 0 1px 0
      rgba(255, 255, 255, 0.08),
    0 0 25px
      rgba(245, 180, 95, 0.08);
}

.internship-large-icon i {

  position: relative;

  z-index: 2;

  font-size: 21px;
}

.internship-large-icon-ring {

  position: absolute;

  width: 34px;
  height: 34px;

  border-radius: 50%;

  border:
    1px solid
    rgba(245, 180, 95, 0.2);
}


/* HEADING */

.internship-overline {

  display: flex;

  align-items: center;

  gap: 7px;

  margin-bottom: 5px;

  color:
    rgba(245, 180, 95, 0.72);

  font-size: 8px;

  font-weight: 800;

  letter-spacing: 1.5px;
}

.internship-heading h3 {

  margin: 0;

  color: #ffffff;

  font-size: 20px;

  font-weight: 750;

  line-height: 1.2;
}

.internship-live-indicator {

  width: 6px;
  height: 6px;

  border-radius: 50%;

  background: #72e6a5;

  box-shadow:
    0 0 0 3px
      rgba(114, 230, 165, 0.06),
    0 0 10px
      rgba(114, 230, 165, 0.55);

  animation:
    internshipPulse
    2s ease-in-out infinite;
}


/* CLOSE */

.internship-close-button {

  width: 32px;
  height: 32px;

  flex: 0 0 32px;

  display: flex;

  align-items: center;
  justify-content: center;

  padding: 0;

  border:
    1px solid
    rgba(255, 255, 255, 0.09);

  border-radius: 9px;

  background:
    rgba(255, 255, 255, 0.035);

  color:
    rgba(255, 255, 255, 0.5);

  cursor: pointer;

  transition: all 0.2s ease;
}

.internship-close-button:hover {

  color: #ffffff;

  border-color:
    rgba(245, 180, 95, 0.4);

  background:
    rgba(245, 180, 95, 0.08);
}


/* =========================================================
   DESCRIPTION
========================================================= */

.internship-description {

  position: relative;

  z-index: 2;

  display: flex;

  gap: 12px;

  margin-top: 17px;

  padding: 13px 14px;

  border-radius: 12px;

  background:
    rgba(255, 255, 255, 0.025);

  border:
    1px solid
    rgba(255, 255, 255, 0.055);
}

.internship-description-icon {

  width: 31px;
  height: 31px;

  flex: 0 0 31px;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 8px;

  color: #f5b45f;

  background:
    rgba(245, 180, 95, 0.08);

  font-size: 11px;
}

.internship-section-label {

  display: block;

  margin-bottom: 4px;

  color:
    rgba(255, 255, 255, 0.32);

  font-size: 6px;

  font-weight: 800;

  letter-spacing: 1.1px;
}

.internship-description p {

  margin: 0;

  color:
    rgba(255, 255, 255, 0.68);

  font-size: 10px;

  line-height: 1.6;
}


/* =========================================================
   DATE INFORMATION
========================================================= */

.internship-main-info {

  position: relative;

  z-index: 2;

  display: grid;

  grid-template-columns:
    1fr 28px 1fr 1fr;

  align-items: center;

  gap: 9px;

  margin-top: 15px;
}

.internship-info-card {

  display: flex;

  align-items: center;

  gap: 10px;

  min-width: 0;

  padding: 12px;

  border-radius: 12px;

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.035),
      rgba(255, 255, 255, 0.015)
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.055);
}

.internship-info-card-icon {

  width: 34px;
  height: 34px;

  flex: 0 0 34px;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 9px;

  background:
    rgba(245, 180, 95, 0.075);

  color: #f5b45f;

  font-size: 12px;
}

.internship-info-card-content {

  min-width: 0;
}

.internship-info-card-content span {

  display: block;

  margin-bottom: 3px;

  color:
    rgba(255, 255, 255, 0.3);

  font-size: 5.5px;

  font-weight: 800;

  letter-spacing: 0.8px;
}

.internship-info-card-content strong {

  display: block;

  overflow: hidden;

  white-space: nowrap;

  text-overflow: ellipsis;

  color:
    rgba(255, 255, 255, 0.9);

  font-size: 10px;

  font-weight: 700;
}


/* DATE CONNECTOR */

.internship-date-connector {

  display: flex;

  align-items: center;

  justify-content: center;

  gap: 2px;

  color:
    rgba(245, 180, 95, 0.45);

  font-size: 7px;
}

.internship-date-connector span {

  width: 4px;
  height: 1px;

  background:
    rgba(245, 180, 95, 0.3);
}


/* =========================================================
   PROGRESS
========================================================= */

.internship-progress-box {

  position: relative;

  z-index: 2;

  margin-top: 15px;

  padding: 14px;

  border-radius: 13px;

  background:
    linear-gradient(
      145deg,
      rgba(245, 180, 95, 0.045),
      rgba(255, 255, 255, 0.018)
    );

  border:
    1px solid
    rgba(245, 180, 95, 0.1);
}

.internship-progress-top {

  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-bottom: 10px;
}

.internship-progress-top > div:first-child strong {

  display: block;

  margin-top: 3px;

  color:
    rgba(255, 255, 255, 0.8);

  font-size: 10px;
}

.internship-progress-percent {

  color: #f5b45f;

  font-size: 18px;

  font-weight: 800;
}


/* TRACK */

.internship-progress-track {

  width: 100%;

  height: 7px;

  overflow: hidden;

  border-radius: 20px;

  background:
    rgba(255, 255, 255, 0.07);
}

.internship-progress-fill {

  position: relative;

  height: 100%;

  min-width: 0;

  border-radius: inherit;

  background:
    linear-gradient(
      90deg,
      #d99548,
      #f5b45f,
      #ffe0a7
    );

  box-shadow:
    0 0 15px
    rgba(245, 180, 95, 0.35);

  transition:
    width 0.5s ease;
}

.internship-progress-fill span {

  position: absolute;

  right: 2px;

  top: 1px;

  width: 5px;
  height: 5px;

  border-radius: 50%;

  background: #ffffff;

  box-shadow:
    0 0 7px #ffffff;
}


/* DATES */

.internship-progress-bottom {

  display: flex;

  justify-content: space-between;

  margin-top: 7px;

  color:
    rgba(255, 255, 255, 0.28);

  font-size: 6px;
}


/* =========================================================
   STATUS
========================================================= */

.internship-bottom-section {

  position: relative;

  z-index: 2;

  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-top: 13px;

  padding: 12px 14px;

  border-radius: 12px;

  background:
    linear-gradient(
      135deg,
      rgba(114, 230, 165, 0.055),
      rgba(114, 230, 165, 0.018)
    );

  border:
    1px solid
    rgba(114, 230, 165, 0.13);
}

.internship-status-main {

  display: flex;

  align-items: center;

  gap: 9px;
}

.internship-status-circle {

  width: 31px;
  height: 31px;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background:
    rgba(114, 230, 165, 0.08);

  border:
    1px solid
    rgba(114, 230, 165, 0.2);

  color: #72e6a5;

  font-size: 10px;
}

.internship-status-text span {

  display: block;

  margin-bottom: 2px;

  color:
    rgba(114, 230, 165, 0.5);

  font-size: 5px;

  font-weight: 800;

  letter-spacing: 0.9px;
}

.internship-status-text strong {

  color: #dff8e9;

  font-size: 10px;

  font-weight: 800;
}


/* REMAINING */

.internship-remaining {

  text-align: right;
}

.internship-remaining span {

  display: block;

  margin-bottom: 2px;

  color:
    rgba(255, 255, 255, 0.3);

  font-size: 5px;

  font-weight: 800;

  letter-spacing: 0.9px;
}

.internship-remaining strong {

  color: #72e6a5;

  font-size: 11px;

  font-weight: 800;
}


/* EXPIRED */

.internship-bottom-expired {

  background:
    linear-gradient(
      135deg,
      rgba(255, 91, 91, 0.055),
      rgba(255, 91, 91, 0.018)
    );

  border-color:
    rgba(255, 91, 91, 0.14);
}

.internship-bottom-expired
  .internship-status-circle {

  background:
    rgba(255, 91, 91, 0.08);

  border-color:
    rgba(255, 91, 91, 0.2);

  color: #ff7373;
}

.internship-bottom-expired
  .internship-status-text span {

  color:
    rgba(255, 115, 115, 0.55);
}

.internship-bottom-expired
  .internship-status-text strong {

  color: #ffdede;
}

.internship-bottom-expired
  .internship-remaining strong {

  color: #ff7373;
}


/* =========================================================
   PAGE CONTENT
========================================================= */

.growniverse-layout-content {

  position: relative;

  width: 100%;

  min-height: 100vh;

  margin: 0;

  padding-top: 88px;

  background: transparent;

  overflow-x: hidden;
}

.growniverse-layout-content.has-internship {

  padding-top: 88px;
}

.growniverse-layout-content main {

  position: relative;

  width: 100%;

  min-height:
    calc(100vh - 88px);

  margin: 0;

  padding: 0;

  background: transparent;
}


/* =========================================================
   BOX SIZING
========================================================= */

.growniverse-user-layout,
.growniverse-user-layout *,
.growniverse-layout-content,
.growniverse-layout-content main {

  box-sizing: border-box;
}


/* =========================================================
   BODY
========================================================= */

body {

  margin: 0;

  padding: 0;

  background: #05070d;
}


/* =========================================================
   NO HORIZONTAL SCROLL
========================================================= */

html,
body {

  max-width: 100%;

  overflow-x: hidden;
}


/* =========================================================
   ANIMATION
========================================================= */

@keyframes internshipExpand {

  from {

    opacity: 0;

    transform:
      translateY(-8px)
      scale(0.97);
  }

  to {

    opacity: 1;

    transform:
      translateY(0)
      scale(1);
  }
}

@keyframes internshipPulse {

  0%,
  100% {

    opacity: 1;

    transform:
      scale(1);
  }

  50% {

    opacity: 0.5;

    transform:
      scale(0.82);
  }
}


/* =========================================================
   TABLET
========================================================= */

@media (max-width: 991px) {

  .growniverse-internship-bar {

    top: 82px;

    right: 12px;
  }

  .internship-full-card {

    width: 600px;
  }

  .internship-main-info {

    grid-template-columns:
      1fr 20px 1fr;

  }

  .internship-duration-card {

    grid-column:
      1 / -1;

  }

}


/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 767px) {

  .growniverse-fixed-header {

    width: 100vw;

    max-width: 100vw;

    left: 0;
    right: 0;
  }


  .growniverse-internship-bar {

    top: 70px;

    right: 8px;
  }


  /* SMALL BUTTON */

  .internship-square-button {

    width: 36px;
    height: 36px;

    border-radius: 9px;
  }

  .internship-square-button i {

    font-size: 14px;
  }

  .internship-square-dot {

    width: 7px;
    height: 7px;

    top: 4px;
    right: 4px;
  }


  /* FULL CARD */

  .internship-full-card {

    width:
      calc(100vw - 16px);

    max-width:
      calc(100vw - 16px);

    padding: 15px;

    border-radius: 16px;
  }


  /* HEADER */

  .internship-large-icon {

    width: 45px;
    height: 45px;

    flex-basis: 45px;

    border-radius: 12px;
  }

  .internship-large-icon i {

    font-size: 17px;
  }

  .internship-large-icon-ring {

    width: 29px;
    height: 29px;
  }

  .internship-overline {

    font-size: 6px;

    letter-spacing: 1px;
  }

  .internship-heading h3 {

    font-size: 15px;
  }

  .internship-close-button {

    width: 29px;
    height: 29px;

    flex-basis: 29px;
  }


  /* DESCRIPTION */

  .internship-description {

    margin-top: 12px;

    padding: 10px;

    gap: 8px;
  }

  .internship-description p {

    font-size: 8px;
  }


  /* DATE GRID */

  .internship-main-info {

    grid-template-columns:
      1fr 1fr;

    gap: 7px;

    margin-top: 10px;
  }

  .internship-date-connector {

    display: none;
  }

  .internship-info-card {

    padding: 9px;

    gap: 7px;
  }

  .internship-info-card-icon {

    width: 29px;
    height: 29px;

    flex-basis: 29px;

    font-size: 10px;
  }

  .internship-info-card-content span {

    font-size: 5px;
  }

  .internship-info-card-content strong {

    font-size: 8px;
  }

  .internship-duration-card {

    grid-column:
      1 / -1;
  }


  /* PROGRESS */

  .internship-progress-box {

    margin-top: 9px;

    padding: 10px;
  }

  .internship-progress-percent {

    font-size: 15px;
  }

  .internship-progress-top
    > div:first-child strong {

    font-size: 8px;
  }


  /* STATUS */

  .internship-bottom-section {

    padding: 9px 10px;
  }

  .internship-status-circle {

    width: 27px;
    height: 27px;

    flex-basis: 27px;
  }

  .internship-status-text strong {

    font-size: 8px;
  }

  .internship-remaining strong {

    font-size: 9px;
  }


  /* CONTENT */

  .growniverse-layout-content,
  .growniverse-layout-content.has-internship {

    padding-top: 70px;
  }

  .growniverse-layout-content main {

    min-height:
      calc(100vh - 70px);
  }

}


/* =========================================================
   VERY SMALL MOBILE
========================================================= */

@media (max-width: 400px) {

  .growniverse-internship-bar {

    top: 68px;

    right: 6px;
  }

  .internship-square-button {

    width: 33px;
    height: 33px;
  }

  .internship-full-card {

    width:
      calc(100vw - 12px);

    max-width:
      calc(100vw - 12px);

    padding: 12px;
  }

  .internship-heading h3 {

    font-size: 13px;
  }

  .internship-info-card {

    padding: 7px;
  }

  .internship-info-card-icon {

    width: 25px;
    height: 25px;

    flex-basis: 25px;
  }

  .internship-info-card-content strong {

    font-size: 7px;
  }

  .internship-description p {

    font-size: 7.5px;
  }

}


/* =========================================================
   REDUCED MOTION
========================================================= */

@media (prefers-reduced-motion: reduce) {

  .internship-square-dot,
  .internship-live-indicator,
  .internship-full-card {

    animation: none;
  }

}

`}
      </style>

    </div>
  );
};

export default UserLayout;